/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
// Third-party
import { css } from '@emotion/react'
import { Spin, App, DatePicker } from 'antd'
import {
  LoadingOutlined,
  EnvironmentOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
// Relative
import Icon from 'src/components/Icon'
import LocationModal from 'src/components/LocationModal'
import { VenueCard } from './components/VenueCard'
import Divider from './components/Divider'
import BlackoutDatesSection, {
  BlackoutDateItem,
  BlackoutDateFormData,
} from './components/BlackoutDatesSection'
import type { Venue, Facility } from './hooks/useSchedulerLocations'
import type {
  BlackoutDate,
  BlackoutDayFormData,
} from './hooks/useSchedulerConstraints'
import type {
  TimeSlot,
  CreateTimeSlotData,
  UpdateTimeSlotData,
} from './hooks/useSchedulerTimeSlots'
import type { Competition } from './hooks/useSchedulerCompetition'

interface ConfigurePageProps {
  competitionId: string
  requestId: string | null
  onRequestCreated: (requestId: string) => void
  createRequest: (scheduleId: string) => Promise<string>
  competition: Competition | null
  refetchCompetition: () => Promise<void>
  venues: Venue[]
  locationsLoading: boolean
  locationsError: Error | null
  refetchLocations: () => Promise<void>
  timeSlots: TimeSlot[]
  timeSlotCounts: Record<string, number>
  timeSlotsLoading: boolean
  createTimeSlot: (
    facilityId: string,
    data: CreateTimeSlotData
  ) => Promise<TimeSlot>
  updateTimeSlot: (
    timeSlotId: string,
    data: UpdateTimeSlotData
  ) => Promise<TimeSlot>
  deleteTimeSlot: (timeSlotId: string) => Promise<void>
  duplicateTimeSlot: (
    timeSlotId: string,
    targetFacilityId?: string
  ) => Promise<TimeSlot>
  getTimeSlotsForFacility: (facilityId: string) => TimeSlot[]
  createTimeSlotForVenue: (
    facilityIds: string[],
    data: CreateTimeSlotData
  ) => Promise<void>
  updateTimeSlotBatch: (
    timeSlotIds: string[],
    data: UpdateTimeSlotData
  ) => Promise<void>
  deleteTimeSlotBatch: (timeSlotIds: string[]) => Promise<void>
  rawBlackoutDates: BlackoutDate[]
  constraintsLoading: boolean
  addBlackoutDate: (
    data: BlackoutDayFormData,
    overrideRequestId?: string
  ) => Promise<unknown>
  deleteConstraint: (constraintId: string) => Promise<void>
  divisions: { id: string; name: string }[]
}

/**
 * Configure Page Component
 */
const ConfigurePage: React.FC<ConfigurePageProps> = ({
  competitionId,
  requestId,
  onRequestCreated,
  createRequest,
  competition,
  refetchCompetition,
  venues,
  locationsLoading,
  locationsError,
  refetchLocations,
  timeSlots,
  timeSlotCounts,
  timeSlotsLoading,
  createTimeSlot,
  updateTimeSlot,
  deleteTimeSlot,
  duplicateTimeSlot,
  getTimeSlotsForFacility,
  createTimeSlotForVenue,
  updateTimeSlotBatch,
  deleteTimeSlotBatch,
  rawBlackoutDates,
  constraintsLoading,
  addBlackoutDate,
  deleteConstraint,
  divisions,
}) => {
  const { message } = App.useApp()

  const organizationId = competition?.organization_id

  // Transform blackout dates to the new format expected by BlackoutDatesSection
  const blackoutDateItems: BlackoutDateItem[] = useMemo(() => {
    return rawBlackoutDates.map((item) => {
      // Use raw YYYY-MM-DD strings directly from the API (already sorted)
      const rawDates = item.rawDates
      const startDate =
        rawDates.length > 0 ? rawDates[0] : dayjs().format('YYYY-MM-DD')
      const endDate =
        rawDates.length > 0
          ? rawDates[rawDates.length - 1]
          : dayjs().format('YYYY-MM-DD')

      return {
        id: item.id,
        constraintId: item.constraintId,
        startDate,
        endDate,
        description: item.description,
        locations: item.venue_uids || [],
        type: '',
        repeat: 'no_repeat',
        divisions: item.division_uids || [],
      }
    })
  }, [rawBlackoutDates])

  // Location options for the blackout dates dropdown
  const locationOptions = useMemo(() => {
    return venues.map((venue) => ({
      label: venue.name || venue.name_full || 'Unknown',
      value: venue.id,
    }))
  }, [venues])

  // Division options for the blackout dates dropdown
  const divisionOptions = useMemo(() => {
    return divisions.map((division) => ({
      label: division.name,
      value: division.id,
    }))
  }, [divisions])

  // Handle adding a blackout date
  const handleAddBlackoutDate = useCallback(
    async (data: BlackoutDateFormData) => {
      // Ensure we have a request ID, create one if needed
      let effectiveRequestId = requestId
      if (!effectiveRequestId) {
        effectiveRequestId = await createRequest(competitionId)
        onRequestCreated(effectiveRequestId)
      }

      if (!data.dateRange) {
        throw new Error('Date range is required')
      }

      // Convert date range to individual date strings (YYYY-MM-DD)
      // Extract as strings first to avoid timezone shifts when iterating Dayjs objects
      const startStr = data.dateRange[0].format('YYYY-MM-DD')
      const endStr = data.dateRange[1].format('YYYY-MM-DD')
      const dateStrings: string[] = []
      let current = dayjs(startStr)
      const end = dayjs(endStr)
      while (current.isBefore(end) || current.isSame(end, 'day')) {
        dateStrings.push(current.format('YYYY-MM-DD'))
        current = current.add(1, 'day')
      }

      await addBlackoutDate(
        {
          dates: dateStrings,
          description: data.description,
          division_uids: data.divisions.length > 0 ? data.divisions : null,
          venue_uids: data.locations.length > 0 ? data.locations : null,
        },
        effectiveRequestId
      )
    },
    [requestId, createRequest, competitionId, onRequestCreated, addBlackoutDate]
  )

  // Handle editing a blackout date (delete + recreate)
  const handleEditBlackoutDate = useCallback(
    async (constraintId: string, data: BlackoutDateFormData) => {
      if (!data.dateRange) {
        throw new Error('Date range is required')
      }

      // Delete the old constraint first
      await deleteConstraint(constraintId)

      // Convert date range to individual date strings
      const startStr = data.dateRange[0].format('YYYY-MM-DD')
      const endStr = data.dateRange[1].format('YYYY-MM-DD')
      const dateStrings: string[] = []
      let current = dayjs(startStr)
      const end = dayjs(endStr)
      while (current.isBefore(end) || current.isSame(end, 'day')) {
        dateStrings.push(current.format('YYYY-MM-DD'))
        current = current.add(1, 'day')
      }

      let effectiveRequestId = requestId
      if (!effectiveRequestId) {
        effectiveRequestId = await createRequest(competitionId)
        onRequestCreated(effectiveRequestId)
      }

      await addBlackoutDate(
        {
          dates: dateStrings,
          description: data.description,
          division_uids: data.divisions.length > 0 ? data.divisions : null,
          venue_uids: data.locations.length > 0 ? data.locations : null,
        },
        effectiveRequestId
      )
    },
    [
      requestId,
      createRequest,
      competitionId,
      onRequestCreated,
      addBlackoutDate,
      deleteConstraint,
    ]
  )

  // Handle deleting a blackout date
  const handleDeleteBlackoutDate = useCallback(
    async (constraintId: string) => {
      await deleteConstraint(constraintId)
    },
    [deleteConstraint]
  )

  // Location modal state
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null)

  // Handle add location
  const handleAddLocation = useCallback(() => {
    setSelectedVenue(null)
    setIsLocationModalOpen(true)
  }, [])

  // Handle edit location
  const handleEditVenue = useCallback((venue: Venue) => {
    setSelectedVenue(venue)
    setIsLocationModalOpen(true)
  }, [])

  // Handle delete venue
  const handleDeleteVenue = useCallback(
    async (venue: Venue) => {
      if (
        !window.confirm(
          `Are you sure you want to remove "${venue.name}" from this competition?`
        )
      ) {
        return
      }

      try {
        await req(`/schedules/${competitionId}/venues/${venue.id}`, {
          method: 'DELETE',
        })
        await refetchLocations()
        message.success('Venue deleted successfully')
      } catch (err) {
        console.error('Failed to delete venue:', err)
        message.error('Failed to delete venue. Please try again.')
      }
    },
    [competitionId, refetchLocations, message]
  )

  const handleDeleteFacility = useCallback(
    async (venue: Venue, facility: Facility) => {
      if (
        !window.confirm(
          `Are you sure you want to remove "${facility.name}" from this location?`
        )
      ) {
        return
      }

      try {
        await req(
          `/schedules/${competitionId}/venues/${venue.id}/facilities/${facility.id}`,
          { method: 'DELETE' }
        )
        await refetchLocations()
        message.success('Surface deleted successfully')
      } catch (err) {
        console.error('Failed to delete facility:', err)
        message.error('Failed to delete surface. Please try again.')
      }
    },
    [competitionId, refetchLocations, message]
  )

  // Handle location modal close
  const handleLocationModalClose = useCallback(() => {
    setIsLocationModalOpen(false)
    setSelectedVenue(null)
  }, [])

  // Handle location modal success
  const handleLocationModalSuccess = useCallback(() => {
    setIsLocationModalOpen(false)
    setSelectedVenue(null)
    refetchLocations()
  }, [refetchLocations])

  // Prepare initial state for location modal edit mode
  const initialVenueData = useMemo(() => {
    if (!selectedVenue) {
      return undefined
    }
    return {
      id: selectedVenue.id,
      name: selectedVenue.name || selectedVenue.name_full || '',
      address_id: selectedVenue.address?.id,
      address_line1: selectedVenue.address?.street_1 || '',
      address_line2: selectedVenue.address?.street_2 || '',
      city: selectedVenue.address?.city || '',
      postcode: selectedVenue.address?.postal_code || '',
      country_id: selectedVenue.address?.country?.id
        ? Number(selectedVenue.address.country.id)
        : undefined,
      province_id: selectedVenue.address?.province?.id
        ? Number(selectedVenue.address.province.id)
        : undefined,
      country: selectedVenue.address?.country?.iso_3166_2 || '',
      province: selectedVenue.address?.province?.iso_3166_2 || '',
    }
  }, [selectedVenue])

  // Game Schedule Date Range state
  // Parse as UTC to avoid timezone shifts (API returns UTC dates like "2026-02-04T00:00:00+00:00")
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(() =>
    competition?.starts_at ? dayjs.utc(competition.starts_at) : null
  )
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(() =>
    competition?.ends_at ? dayjs.utc(competition.ends_at) : null
  )

  // Sync dates when competition data loads
  React.useEffect(() => {
    if (competition?.starts_at && !startDate) {
      setStartDate(dayjs.utc(competition.starts_at))
    }
    if (competition?.ends_at && !endDate) {
      setEndDate(dayjs.utc(competition.ends_at))
    }
  }, [competition])

  // Auto-save date range to competition when both dates are valid
  const isInitialMount = useRef(true)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Skip the initial mount / sync from competition data
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Both dates must be defined
    if (!startDate || !endDate) {
      return
    }

    // End date must be on or after start date
    if (endDate.isBefore(startDate, 'day')) {
      return
    }

    const newStartsAt = startDate.format('YYYY-MM-DD')
    const newEndsAt = endDate.format('YYYY-MM-DD')

    // Skip if dates haven't actually changed from the competition
    const currentStartsAt = competition?.starts_at
      ? dayjs.utc(competition.starts_at).format('YYYY-MM-DD')
      : null
    const currentEndsAt = competition?.ends_at
      ? dayjs.utc(competition.ends_at).format('YYYY-MM-DD')
      : null

    if (newStartsAt === currentStartsAt && newEndsAt === currentEndsAt) {
      return
    }

    // Debounce the save
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        await req(`/schedules/${competitionId}`, {
          method: 'PUT',
          body: JSON.stringify({
            starts_at: newStartsAt,
            ends_at: newEndsAt,
          }),
        })
        message.success('Date range updated')
        // Refresh the competition object so other components see the new dates
        await refetchCompetition()
      } catch (err) {
        console.error('Failed to update date range:', err)
        message.error('Failed to update date range')
      }
    }, 800)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [startDate, endDate])

  return (
    <div
      css={css`
        min-height: 100%;
      `}
    >
      <div
        css={css`
          max-width: 1440px;
          margin: 0 auto;
        `}
      >
        {/* Game Schedule Date Range Section */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 40px;
            width: 100%;
            margin-bottom: 24px;
          `}
        >
          {/* Left side: Title + Description */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
              flex: 1;
            `}
          >
            <h2
              css={css`
                margin: 0;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 600;
                line-height: 16.8px;
                letter-spacing: 1.68px;
                text-transform: uppercase;
                color: ${colors.NEUTRAL_0};
              `}
            >
              Game Schedule Date Range
            </h2>
            <div
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 12px;
                font-weight: 400;
                line-height: 17.4px;
                color: ${colors.NEUTRAL_50};
              `}
            >
              <p
                css={css`
                  margin: 0;
                `}
              >
                This range defines the window the scheduler will use to place
                games.
              </p>
              <p
                css={css`
                  margin: 0;
                `}
              >
                Games will only be generated within this date range, using
                available time slots.
              </p>
            </div>
          </div>

          {/* Right side: Date Pickers */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 32px;
              flex-shrink: 0;
            `}
          >
            {/* Start Date */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 20.3px;
                  color: ${colors.NEUTRAL_0};
                `}
              >
                Start Date
              </span>
              <DatePicker
                value={startDate}
                onChange={(date) => setStartDate(date)}
                format='MMM DD, YYYY'
                suffixIcon={
                  <Icon
                    name='calendar'
                    faType='far'
                    fontSize={18}
                    color={colors.NEUTRAL_0}
                  />
                }
                allowClear={false}
                css={css`
                  && {
                    background-color: #293241;
                    border: 1px solid #3a4659;
                    border-radius: 8px;
                    height: 40px;
                    padding: 8px 16px;
                    min-width: 170px;

                    .ant-picker-input > input {
                      font-family: 'Inter', sans-serif;
                      font-size: 14px;
                      font-weight: 500;
                      line-height: 20.3px;
                      color: ${colors.NEUTRAL_0};
                    }

                    &:hover,
                    &.ant-picker-focused {
                      border-color: ${colors.PRIMARY_300};
                    }

                    .ant-picker-suffix {
                      color: ${colors.NEUTRAL_0};
                    }
                  }
                `}
              />
            </div>

            {/* End Date */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 20.3px;
                  color: ${colors.NEUTRAL_0};
                `}
              >
                End Date
              </span>
              <DatePicker
                value={endDate}
                onChange={(date) => setEndDate(date)}
                format='MMM DD, YYYY'
                suffixIcon={
                  <Icon
                    name='calendar'
                    faType='far'
                    fontSize={18}
                    color={colors.NEUTRAL_0}
                  />
                }
                allowClear={false}
                css={css`
                  && {
                    background-color: #293241;
                    border: 1px solid #3a4659;
                    border-radius: 8px;
                    height: 40px;
                    padding: 8px 16px;
                    min-width: 170px;

                    .ant-picker-input > input {
                      font-family: 'Inter', sans-serif;
                      font-size: 14px;
                      font-weight: 500;
                      line-height: 20.3px;
                      color: ${colors.NEUTRAL_0};
                    }

                    &:hover,
                    &.ant-picker-focused {
                      border-color: ${colors.PRIMARY_300};
                    }

                    .ant-picker-suffix {
                      color: ${colors.NEUTRAL_0};
                    }
                  }
                `}
              />
            </div>
          </div>
        </div>

        {/* Divider between Date Range and Locations */}
        <Divider
          css={css`
            margin-bottom: 24px;
          `}
        />

        {/* Locations & Time Slots Section Header */}
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            width: 100%;
            height: 32px;
            margin-bottom: 16px;
          `}
        >
          {/* Section Title */}
          <h2
            css={css`
              flex: 1;
              margin: 0;
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 600;
              line-height: 16.8px;
              letter-spacing: 1.68px;
              text-transform: uppercase;
              color: ${colors.NEUTRAL_0};
            `}
          >
            Locations & Time Slots
          </h2>

          {/* Action Buttons */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            {/* Add Button */}
            <button
              type='button'
              onClick={handleAddLocation}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                background: transparent;
                border: 1px solid ${colors.PRIMARY_300};
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover {
                  background: ${colors.PRIMARY_300}20;
                }
              `}
            >
              <Icon name='plus' fontSize={24} color={colors.PRIMARY_300} />
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 20.3px;
                  color: ${colors.PRIMARY_300};
                `}
              >
                Add
              </span>
            </button>
          </div>
        </div>

        {/* Content Area */}
        {locationsLoading ? (
          // Loading state
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 48px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
              border: 1px solid ${colors.SECONDARY_200};
            `}
          >
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
            <span
              css={css`
                margin-left: 12px;
                color: ${colors.NEUTRAL_50};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
              `}
            >
              Loading locations...
            </span>
          </div>
        ) : locationsError ? (
          // Error state
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 48px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
              border: 1px solid ${colors.ERROR};
            `}
          >
            <span
              css={css`
                color: ${colors.ERROR};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                margin-bottom: 8px;
              `}
            >
              Failed to load locations
            </span>
            <button
              type='button'
              onClick={() => refetchLocations()}
              css={css`
                background: ${colors.PRIMARY};
                border: none;
                color: ${colors.WHITE};
                padding: 8px 16px;
                border-radius: 4px;
                cursor: pointer;
                font-family: 'Inter', sans-serif;
                font-size: 14px;

                &:hover {
                  background: ${colors.PRIMARY_200};
                }
              `}
            >
              Retry
            </button>
          </div>
        ) : venues.length === 0 ? (
          // Empty state
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 48px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
              border: 1px dashed ${colors.SECONDARY_200};
            `}
          >
            <EnvironmentOutlined
              css={css`
                font-size: 48px;
                color: ${colors.NEUTRAL_100};
                margin-bottom: 16px;
              `}
            />
            <span
              css={css`
                color: ${colors.NEUTRAL_50};
                font-family: 'Inter', sans-serif;
                font-size: 16px;
                font-weight: 500;
                margin-bottom: 8px;
              `}
            >
              No locations configured
            </span>
            <span
              css={css`
                color: ${colors.NEUTRAL_100};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                margin-bottom: 16px;
                text-align: center;
              `}
            >
              Add locations and surfaces where games will be played
            </span>
            <button
              type='button'
              onClick={handleAddLocation}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                background: ${colors.PRIMARY};
                border: none;
                color: ${colors.WHITE};
                padding: 10px 20px;
                border-radius: 4px;
                cursor: pointer;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 500;

                &:hover {
                  background: ${colors.PRIMARY_200};
                }
              `}
            >
              <PlusOutlined />
              Add Location
            </button>
          </div>
        ) : (
          // Venue cards
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
            `}
          >
            {venues.map((venue) => (
              <VenueCard
                key={venue.id}
                venue={venue}
                competitionId={competitionId}
                timeSlots={timeSlots}
                timeSlotCounts={timeSlotCounts}
                timeSlotsLoading={timeSlotsLoading}
                divisions={divisions}
                onEditVenue={handleEditVenue}
                onDeleteVenue={handleDeleteVenue}
                onDeleteFacility={handleDeleteFacility}
                onAddTimeSlot={createTimeSlot}
                onUpdateTimeSlot={updateTimeSlot}
                onDeleteTimeSlot={deleteTimeSlot}
                onDuplicateTimeSlot={duplicateTimeSlot}
                getTimeSlotsForFacility={getTimeSlotsForFacility}
                onAddTimeSlotForVenue={createTimeSlotForVenue}
                onUpdateTimeSlotBatch={updateTimeSlotBatch}
                onDeleteTimeSlotBatch={deleteTimeSlotBatch}
              />
            ))}
          </div>
        )}

        {/* Section Divider */}
        <Divider
          css={css`
            margin-top: 24px;
            margin-bottom: 24px;
          `}
        />

        {/* Blackout Dates Section */}
        <BlackoutDatesSection
          blackoutDates={blackoutDateItems}
          locationOptions={locationOptions}
          divisionOptions={divisionOptions}
          loading={constraintsLoading}
          onAddBlackoutDate={handleAddBlackoutDate}
          onEditBlackoutDate={handleEditBlackoutDate}
          onDeleteBlackoutDate={handleDeleteBlackoutDate}
        />
      </div>

      {/* Location Modal */}
      {organizationId && (
        <LocationModal
          isOpen={isLocationModalOpen}
          onClose={handleLocationModalClose}
          organizationId={organizationId}
          onSuccess={handleLocationModalSuccess}
          initialState={initialVenueData}
          initialFacilities={selectedVenue?.facilities || []}
        />
      )}
    </div>
  )
}

export default ConfigurePage
